import { addDays, format, isAfter, isBefore } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'

import TimerModal from '@/contentComponents/modal/B2gContractModal/CommonTeacher/TimerModal'
import ContractModal from '@/contentComponents/modal/B2gContractModal/RootTeacher/ContractModal'
import { CustomerTypeChangeModal } from '@/contentComponents/modal/CustomerTypeChangeModal'
import NoticeBoardModal from '@/contentComponents/modal/NoticeBoardModal/NoticeBoardModal.tsx'
import RequiredConfirmModal from '@/contentComponents/modal/RequiredConfirmModal/RequiredConfirmModal'
import UpcomingStudentContractPlanModal from '@/contentComponents/modal/UpcomingStudentContractPlanModal/UpcomingStudentContractPlanModal'
import { resolveNoticeBrandStatus } from '@/controller/notice.controller'
import { NoticeBoardColl } from '@/dto/Notice/NoticeBoard.ts'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import modalService from '@/service/modal.service'
import popupService from '@/service/popup.service'
import { localStorageService } from '@/service/storage.service'
import { remoteStorageService } from '@/utils/axios/remoteStorage'
import { envUtils } from '@/utils/envUtils'

export const usePopup = (_off = false) => {
  const [off, setOff] = useState(!!_off)
  const hasShownNoticeBoardRef = useRef(false)

  const togglePopup = () => {
    setOff((prev) => {
      if (!envUtils.isLive) {
        localStorageService.setItem('POPUP_VISIBILITY_DEV_ONLY')(!prev)
      }
      if (!prev) {
        modalService.closeModal()
      }
      return !prev
    })
  }

  const handleNoticeBoardOpen = useCallback((notices: NoticeBoardColl) => {
    modalService.openModal(<NoticeBoardModal notices={notices} />, {
      hasCloseButton: false,
    })
  }, [])

  const handleCustomerTypeChangePopupOpen = useCallback(() => {
    modalService.openModal(<CustomerTypeChangeModal />, {
      hasCloseButton: false,
      isCloseable: false,
    })
  }, [])

  const fetchModals = useCallback(() => {
    if (commonRepo.user && commonRepo.academy) {
      if (commonRepo.academy.needCustomerTypeChange) {
        handleCustomerTypeChangePopupOpen()
        return
      }
      // 온보딩 팝업 리스트 전체 불러오기
      popupService.fetchAndSetOnboardingPopups(commonRepo.user.teacherId)

      // 팝업 보지 않기 기간 확인.
      const teacherId = commonRepo.user.teacherId
      const today = new Date()

      /**
       * @deprecated
       * 하위 호환성을 위한 코드
       * 2025-08-13 배포 이후 삭제 가능
       */
      const deprecated_doNotShowAgainNoticeModalExpireDate = localStorage.getItem(
        `DO_NOT_SHOW_AGAIN_NOTICE_BOARD_DATE_${teacherId}`,
      )
        ? new Date(localStorage.getItem(`DO_NOT_SHOW_AGAIN_NOTICE_BOARD_DATE_${teacherId}`)!)
        : null

      const doNotShowAgainNotice = localStorageService.getItem(
        `PERSIST_DO_NOT_SHOW_AGAIN_NOTICE_BOARD_${teacherId}`,
      )

      const showDoNotShowAgainNotice =
        doNotShowAgainNotice?.teacherId === teacherId && doNotShowAgainNotice
          ? isAfter(today, new Date(doNotShowAgainNotice.expiredAt))
          : true

      if (showDoNotShowAgainNotice) {
        localStorageService.removeItem(`PERSIST_DO_NOT_SHOW_AGAIN_NOTICE_BOARD_${teacherId}`)
      }

      const showNoticeBoard = deprecated_doNotShowAgainNoticeModalExpireDate
        ? isAfter(today, deprecated_doNotShowAgainNoticeModalExpireDate)
        : showDoNotShowAgainNotice

      // 팝업 띄우기
      popupService
        .getFirstCommonPopup(
          commonRepo.user.teacherId,
          commonRepo.academy?.status,
          commonRepo.academy?.metadata,
          commonRepo.academy.authority,
          resolveNoticeBrandStatus({
            isCreverse: commonRepo.isCreverse,
            isVine: commonRepo.isVine,
            isSchoolflat: commonRepo.isSchoolflat,
          }),
        )
        .then((popups) => {
          if (!popups) {
            return
          }
          // effect 시점엔 /login 이어도 응답 도착 시점엔 브릿지로 이동해 있을 수 있어 노출 직전 재확인
          if (window.location.pathname === RouteName.onlineOnboardingBridge) {
            return
          }
          if (hasShownNoticeBoardRef.current) return
          hasShownNoticeBoardRef.current = true

          if (!showNoticeBoard || popups.length === 0) return
          handleNoticeBoardOpen(new NoticeBoardColl(popups))
        })
        .catch((e) => {
          console.error(e)
          //toast는 의도적으로 띄우지 않는다.
        })
    }
  }, [
    handleCustomerTypeChangePopupOpen,
    handleNoticeBoardOpen,
    commonRepo.academy,
    commonRepo.user,
  ])

  useEffect(() => {
    if (!envUtils.isLive && off) return
    const pathname = window.location.pathname

    // 서버 점검중일땐 띄우지 않기
    if (pathname === RouteName.serverMaintenance) return
    // Tally 임베드 페이지에서는 공지/계약 모달이 새 탭에서 뜨지 않도록 함
    if (pathname === RouteName.tallyEmbed) return
    // 온라인 온보딩 브릿지는 외부로 즉시 이동하는 경유 페이지라 팝업을 띄우지 않음
    if (pathname === RouteName.onlineOnboardingBridge) return
    if (!commonRepo.user || !commonRepo.academy) {
      hasShownNoticeBoardRef.current = false
      return
    }

    remoteStorageService
      .get('STARTED_NEW_DESIGN_FLAT')
      .then((response) => {
        if (!response) {
          modalService.openModal(<RequiredConfirmModal />, {
            hasCloseButton: false,
            isCloseable: false,
            modalStyle: {
              content: {
                backgroundColor: 'transparent',
              },
            },
          })

          return
        }
      })
      .finally(() => {
        fetchModals()
      })
  }, [off, fetchModals])

  useEffect(() => {
    const pathname = window.location.pathname

    // 서버 점검중일땐 띄우지 않기
    if (pathname === RouteName.serverMaintenance) return

    // Tally 임베드 페이지에서는 공지/계약 모달이 새 탭에서 뜨지 않도록 함
    if (pathname === RouteName.tallyEmbed) return

    // 주문 성공 실패 페이지는 팝업 띄우지 않음
    if (pathname === RouteName.podMallOrderSuccess || pathname === RouteName.podMallOrderFailure)
      return

    // 온라인 온보딩 브릿지는 외부로 즉시 이동하는 경유 페이지라 팝업을 띄우지 않음
    if (pathname === RouteName.onlineOnboardingBridge) return

    // 계약서가 발송되었지만 계약서에 서명을 안한 상태
    if (
      commonRepo.isSchoolflat &&
      commonRepo.b2gContract?.status &&
      commonRepo.b2gContract?.isUnSigned
    ) {
      // 관리자선생님이면 계약안내 모달 띄우기
      if (commonRepo.user?.isAdmin) {
        modalService.openModal(<ContractModal onClick={fetchModals} />, {
          hasCloseButton: false,
          isCloseable: false,
        })
        return
      } else {
        // 일반선생님이면 안내 모달 띄우기 및 그 팝업을 닫으면 기능팝업 호출
        modalService.openModal(<TimerModal onClick={fetchModals} />, {
          hasCloseButton: false,
        })
        return
      }
    }

    if (
      commonRepo.isSchoolflat &&
      commonRepo.currentStudentContractPlan &&
      commonRepo.nextStudentContractPlans
    ) {
      // 다음 이용권 시작일이 오늘을 포함해서 7일 이내이면 팝업 띄우기
      const isShowDate = isBefore(
        addDays(commonRepo.nextStudentContractPlans[0].contractStartDate, -8),
        new Date(),
      )
      const localStorageOption = localStorageService.getItem('UPDATE_CONTRACT_MODAL_OPTION')

      if (
        isShowDate &&
        (!localStorageOption ||
          (localStorageOption &&
            !localStorageOption.teacherIds.includes(commonRepo.user!.teacherId) &&
            format(new Date(localStorageOption.contractStartDate), 'yyyy.MM.dd') !==
              format(commonRepo.nextStudentContractPlans[0].contractStartDate, 'yyyy.MM.dd')))
      ) {
        modalService.openModal(<UpcomingStudentContractPlanModal onClick={fetchModals} />, {
          hasCloseButton: false,
          isCloseable: false,
        })
        return
      }
    }
  }, [
    commonRepo.isSchoolflat,
    commonRepo.b2gContract,
    commonRepo.user?.isAdmin,
    commonRepo.currentStudentContractPlan,
    commonRepo.nextStudentContractPlans,
    commonRepo.user?.teacherId,
    fetchModals,
  ])

  return { off, togglePopup }
}
